// Izvoz bruto bilance i kartice konta u Excel.
//
// Isti raspored kao pdf: ista zaglavlja, iste kolone, isti redci istim redom,
// razina se vidi po uvlaci u nazivu, zbrojevi na dnu. Radi se izravno preko
// `xlsx` a ne preko json-as-xlsx jer objedinjeni naslov iznad para kolona trazi
// spajanje celija, a to json-as-xlsx ne zna.
//
// Iznosi se pisu kao brojevi, ne kao vec formatiran tekst - inace se u Excelu
// ne moze nista zbrojiti. Oblik s tockom za tisuce i zarezom za decimale daje
// format celije. Nula ostaje prazna celija, kao sto je na papiru crtica.
//
// Saldo u Excelu ide s predznakom umjesto oznake D/P. Broj s minusom se moze
// zbrajati i sortirati, "1.234,00 P" ne moze.

import XLSX from 'xlsx'
import dayjs from 'dayjs'

const FMT = '#,##0.00'

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

// Nula se ne pise - prazna celija cita se brze i ne smeta zbrajanju.
const iznos = (v) => (!v || Math.abs(v) < 0.005 ? null : Math.round(num(v) * 100) / 100)

const datumTekst = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '')

// Za tekst u zaglavlju, gdje broj ne moze biti zaseban stupac.
const novacTekst = (v) => num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')

const naziviFirme = (company) => [
  company.CompanyNameShort || company.CompanyName || '',
  [company.CompanyStreet, company.CompanyBuildingNameNumber].filter(x => x).join(' ') +
    (company.CompanyPostcode || company.CompanyCity
      ? ', ' + [company.CompanyPostcode, company.CompanyCity].filter(x => x).join(' ')
      : ''),
  company.CompanyVATID ? 'OIB: ' + company.CompanyVATID : ''
]

// Broj celija se formatira tek nakon sto je list izgradjen - aoa_to_sheet ne
// prima format po celiji.
const formatirajBrojeve = (ws, odRetka, prvaKolona) => {
  const raspon = XLSX.utils.decode_range(ws['!ref'])
  for (let r = odRetka; r <= raspon.e.r; r += 1) {
    for (let c = prvaKolona; c <= raspon.e.c; c += 1) {
      const adr = XLSX.utils.encode_cell({r: r, c: c})
      const cell = ws[adr]
      if (cell && cell.t === 'n') { cell.z = FMT }
    }
  }
}

const spremi = (ws, imeLista, imeDatoteke) => {
  const wb = XLSX.utils.book_new()
  // Excel ne da ime lista duze od 31 znaka ni sa znakovima : \ / ? * [ ]
  const sigurno = String(imeLista).replace(/[:\\/?*[\]]/g, '_').substring(0, 31)
  XLSX.utils.book_append_sheet(wb, ws, sigurno)
  XLSX.writeFile(wb, imeDatoteke + '.xlsx')
}

// ---------------------------------------------------------------------------
// Bruto bilanca
// ---------------------------------------------------------------------------

const koloneBilance = (detaljno) => (detaljno
  ? [
    {head: 'Konto', w: 10},
    {head: 'Naziv konta', w: 46},
    {head: 'Duguje', grupa: 'Početno stanje', w: 15},
    {head: 'Potražuje', grupa: 'Početno stanje', w: 15},
    {head: 'Duguje', grupa: 'Promet u razdoblju', w: 15},
    {head: 'Potražuje', grupa: 'Promet u razdoblju', w: 15},
    {head: 'Duguje', grupa: 'Ukupno', w: 15},
    {head: 'Potražuje', grupa: 'Ukupno', w: 15},
    {head: 'Saldo', grupa: 'Ukupno', w: 16}
  ]
  : [
    {head: 'Konto', w: 10},
    {head: 'Naziv konta', w: 60},
    {head: 'Duguje', w: 16},
    {head: 'Potražuje', w: 16},
    {head: 'Saldo', w: 17}
  ])

// Uvlaka razine ide razmacima u naziv - Excel bez stilova nema drugog nacina
// pokazati da konto pripada sintetici.
const uvuci = (level, name) => {
  const razmaci = ['', '  ', '    ', '      '][level - 1] || ''
  return razmaci + (level === 1 ? String(name || '').toUpperCase() : (name || ''))
}

/**
 * @param {Object} ctx  isti kao za pdf: redci, zbroj, detaljno, company,
 *                      fromDate, toDate, fromAccount, toAccount, valuta,
 *                      razinaOpis, brojKonta, businessYear
 */
export const exportBalanceXlsx = (ctx) => {
  const redci = ctx.redci || []
  const zbroj = ctx.zbroj || {}
  const detaljno = ctx.detaljno === true
  const cols = koloneBilance(detaljno)
  const valuta = ctx.valuta || 'EUR'
  const company = ctx.company || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const prazno = cols.map(() => null)
  const aoa = []
  const firma = naziviFirme(company)

  aoa.push(['Bruto bilanca'].concat(prazno.slice(1)))
  aoa.push([firma[0]].concat(prazno.slice(1)))
  aoa.push([firma[1]].concat(prazno.slice(1)))
  aoa.push([firma[2]].concat(prazno.slice(1)))
  aoa.push([
    valuta + '   ' + datumTekst(ctx.fromDate) + ' – ' + datumTekst(ctx.toDate) +
    '   konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '') +
    '   ' + (ctx.razinaOpis || '') + ', ' + (detaljno ? 'detaljno' : 'sažeto')
  ].concat(prazno.slice(1)))
  aoa.push(['Ispis ' + trenutak + '   ·   ' + redci.length + ' redaka   ·   ' +
    (ctx.brojKonta || 0) + ' konta'].concat(prazno.slice(1)))
  aoa.push(prazno.slice())

  const merges = []
  const redakGrupa = aoa.length

  if (detaljno) {
    const grupni = cols.map(c => c.grupa || null)
    aoa.push(grupni.map((g, i) => (i > 0 && grupni[i - 1] === g ? null : g)))
    let i = 0
    while (i < cols.length) {
      const g = cols[i].grupa
      if (!g) { i += 1; continue }
      let span = 0
      while (i + span < cols.length && cols[i + span].grupa === g) { span += 1 }
      if (span > 1) {
        merges.push({s: {r: redakGrupa, c: i}, e: {r: redakGrupa, c: i + span - 1}})
      }
      i += span
    }
  }

  const redakNaslova = aoa.length
  aoa.push(cols.map(c => c.head))

  redci.forEach(r => {
    const red = [r.code, uvuci(r.level, r.name)]
    if (detaljno) {
      red.push(iznos(r.psD), iznos(r.psP), iznos(r.prD), iznos(r.prP))
    }
    red.push(iznos(r.totD), iznos(r.totP), iznos(r.saldo))
    aoa.push(red)
  })

  const ukupno = ['', 'UKUPNO ' + valuta]
  if (detaljno) {
    ukupno.push(num(zbroj.psD), num(zbroj.psP), num(zbroj.prD), num(zbroj.prP))
  }
  ukupno.push(num(zbroj.totD), num(zbroj.totP), num(zbroj.saldo))
  aoa.push(ukupno)

  aoa.push(prazno.slice())
  aoa.push([Math.abs(num(zbroj.saldo)) < 0.005
    ? 'Duguje = Potražuje, bilanca je u ravnoteži.'
    : 'PAŽNJA: bilanca nije u ravnoteži, razlika ' +
      (Math.round(num(zbroj.saldo) * 100) / 100)].concat(prazno.slice(1)))

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({wch: c.w}))
  if (merges.length > 0) { ws['!merges'] = merges }
  // Filtar na naslovima kolona. Zamrzavanje redaka bi ovdje bilo korisnije, ali
  // ova gradnja `xlsx` ga kod zapisa tiho preskace, pa se ne postavlja.
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range(
      {r: redakNaslova, c: 0},
      {r: redakNaslova + redci.length, c: cols.length - 1}
    )
  }
  formatirajBrojeve(ws, redakNaslova + 1, 2)

  const godina = ctx.businessYear || dayjs(ctx.toDate).year() || ''
  spremi(
    ws,
    'Bruto bilanca ' + godina,
    (company.CompanyNameShort || 'Bilanca') + '_BrutoBilanca_' + dayjs().format('DD_MM_YYYY_HHmm')
  )
}

// ---------------------------------------------------------------------------
// Kartica konta
// ---------------------------------------------------------------------------

const koloneKartice = () => [
  {head: 'Datum knjiženja', w: 16},
  {head: 'Dokument', w: 14},
  {head: 'Veza', w: 14},
  {head: 'Partner', w: 34},
  {head: 'Opis', w: 34},
  {head: 'Duguje', w: 15},
  {head: 'Potražuje', w: 15},
  {head: 'Saldo', w: 16}
]

/**
 * @param {Object} ctx  konto, stavke, psDebit, psCredit, prometDebit,
 *                      prometCredit, company, fromDate, toDate, valuta
 */
export const exportAccountCardXlsx = (ctx) => {
  const cols = koloneKartice()
  const stavke = ctx.stavke || []
  const konto = ctx.konto || {}
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')
  const psD = num(ctx.psDebit)
  const psP = num(ctx.psCredit)
  const prD = num(ctx.prometDebit)
  const prP = num(ctx.prometCredit)

  const prazno = cols.map(() => null)
  const aoa = []
  const firma = naziviFirme(company)

  aoa.push(['Kartica konta ' + (konto.account4 || '')].concat(prazno.slice(1)))
  aoa.push([konto.account4Name || ''].concat(prazno.slice(1)))
  aoa.push([firma[0]].concat(prazno.slice(1)))
  aoa.push([firma[1]].concat(prazno.slice(1)))
  aoa.push([valuta + '   ' + datumTekst(ctx.fromDate) + ' – ' + datumTekst(ctx.toDate)].concat(prazno.slice(1)))
  aoa.push(['Ispis ' + trenutak + '   ·   ' + stavke.length + ' stavaka'].concat(prazno.slice(1)))
  aoa.push(prazno.slice())

  const redakNaslova = aoa.length
  aoa.push(cols.map(c => c.head))

  aoa.push(['', '', '', 'Početno stanje', '', iznos(psD), iznos(psP), psD - psP])

  stavke.forEach(s => {
    aoa.push([
      s.BookingDate ? dayjs(s.BookingDate).format('DD.MM.YYYY') : '',
      s.dokument || '',
      s.veza || '',
      s.partnerName || '',
      s.Description || '',
      iznos(s.Debit),
      iznos(s.Credit),
      Math.round(num(s.saldo) * 100) / 100
    ])
  })

  aoa.push(['', '', '', 'Promet u razdoblju', '', prD, prP, prD - prP])
  aoa.push(['', '', '', 'Ukupno', '', psD + prD, psP + prP, (psD + prD) - (psP + prP)])

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({wch: c.w}))
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range(
      {r: redakNaslova, c: 0},
      {r: redakNaslova + 1 + stavke.length, c: cols.length - 1}
    )
  }
  formatirajBrojeve(ws, redakNaslova + 1, 5)

  spremi(
    ws,
    'Konto ' + (konto.account4 || ''),
    (company.CompanyNameShort || 'Kartica') + '_Konto_' + (konto.account4 || '') +
      '_' + dayjs().format('DD_MM_YYYY_HHmm')
  )
}

// ---------------------------------------------------------------------------
// Financijski izvjestaj - racun dobitka i gubitka i GFI-POD bilanca
//
// Oba daju retke istog oblika pa dijele isti izvoz; razlikuje ih naslov.
// ---------------------------------------------------------------------------

// Kolona s kontima ide u izvoz samo ako se izricito zatrazi - iz nje se cita
// kako je RDG sastavljen, pa po zadanom izostaje.
const koloneIzvjestaja = (sKontima) => [
  {key: 'col1', head: 'Ozn.', w: 7},
  {key: 'col2', head: 'Konta iz RRiF-ovog plana', w: 34},
  {key: 'naziv', head: 'Naziv pozicije', w: 62},
  {key: 'col5', head: 'Prethodna godina', w: 18},
  {key: 'col6', head: 'Tekuća godina', w: 18}
].filter(c => sKontima === true || c.key !== 'col2')

// Razina se vidi po uvlaci, isto kao u bilanci.
const uvuciIzvjestaj = (r) => {
  const naziv = String(r.col3 || '').replace(/^[\t\s]+/, '')
  if (r.header) { return naziv.toUpperCase() }
  if (r.bold) { return '  ' + naziv }
  return '    ' + naziv
}

/**
 * @param {Object} ctx  redci (gotovi iz rdg2), company, fromDate, toDate,
 *                      valuta, tekucaGodina, prethodnaGodina, naslov, sKontima
 */
export const exportStatementXlsx = (ctx) => {
  const cols = koloneIzvjestaja(ctx.sKontima === true)
  const redci = ctx.redci || []
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const naslov = ctx.naslov || 'Račun dobitka i gubitka'
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  // Zadnje dvije kolone su uvijek godine, bez obzira ima li kolone s kontima.
  cols[cols.length - 2].head = String(ctx.prethodnaGodina || 'Prethodna godina')
  cols[cols.length - 1].head = String(ctx.tekucaGodina || 'Tekuća godina')

  const prazno = cols.map(() => null)
  const aoa = []
  const firma = naziviFirme(company)

  aoa.push([naslov].concat(prazno.slice(1)))
  aoa.push([firma[0]].concat(prazno.slice(1)))
  aoa.push([firma[1]].concat(prazno.slice(1)))
  aoa.push([firma[2]].concat(prazno.slice(1)))
  aoa.push([valuta + '   ' + datumTekst(ctx.fromDate) + ' – ' + datumTekst(ctx.toDate) +
    '   po RRiF-ovom kontnom planu'].concat(prazno.slice(1)))
  aoa.push(['Ispis ' + trenutak + '   ·   ' + redci.length + ' pozicija'].concat(prazno.slice(1)))
  aoa.push(prazno.slice())

  const redakNaslova = aoa.length
  aoa.push(cols.map(c => c.head))

  redci.forEach(r => {
    aoa.push(cols.map(c => {
      if (c.key === 'naziv') { return uvuciIzvjestaj(r) }
      if (c.key === 'col5' || c.key === 'col6') { return iznos(r[c.key]) }
      return r[c.key] || ''
    }))
  })

  // Ime lista i datoteke po vrsti izvjestaja.
  const kratica = naslov.indexOf('GFI') !== -1 ? 'GFI-POD' : 'RDG'

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({wch: c.w}))
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range(
      {r: redakNaslova, c: 0},
      {r: redakNaslova + redci.length, c: cols.length - 1}
    )
  }
  formatirajBrojeve(ws, redakNaslova + 1, cols.length - 2)

  spremi(
    ws,
    kratica + ' ' + (ctx.tekucaGodina || ''),
    (company.CompanyNameShort || kratica) + '_' + kratica + '_' + dayjs().format('DD_MM_YYYY_HHmm')
  )
}

// ---------------------------------------------------------------------------
// Kartica partnera
// ---------------------------------------------------------------------------

const kolonePartnera = () => [
  {key: 'dokument', head: 'Dokument', w: 14},
  {key: 'veza', head: 'Veza', w: 13},
  {key: 'datum', head: 'Datum knjiženja', w: 16},
  {key: 'datumDok', head: 'Datum dokumenta', w: 16},
  {key: 'dospijece', head: 'Dospijeće', w: 13},
  {key: 'dana', head: 'Dana', w: 7},
  {key: 'opis', head: 'Opis', w: 40},
  {key: 'konto', head: 'Konto', w: 10},
  {key: 'debit', head: 'Duguje', w: 14},
  {key: 'credit', head: 'Potražuje', w: 14},
  {key: 'saldo', head: 'Saldo', w: 15}
]

/**
 * @param {Object} ctx  isti kao za pdf kartice partnera
 *
 * U Excel idu sve stavke, i one iz pocetnog stanja - tamo se filtrira i zbraja,
 * pa skupljeni redak nema smisla. Pripadnost grupi nosi kolona Opis.
 */
export const exportPartnerCardXlsx = (ctx) => {
  const cols = kolonePartnera()
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const partner = ctx.partner || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const prazno = cols.map(() => null)
  const aoa = []
  const firma = naziviFirme(company)

  aoa.push(['Kartica partnera'].concat(prazno.slice(1)))
  aoa.push([partner.ime || ''].concat(prazno.slice(1)))
  aoa.push([partner.oib ? 'OIB: ' + partner.oib : ''].concat(prazno.slice(1)))
  aoa.push([firma[0]].concat(prazno.slice(1)))
  aoa.push([valuta + '   ' + datumTekst(ctx.fromDate) + ' – ' + datumTekst(ctx.toDate) +
    '   konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '')].concat(prazno.slice(1)))
  aoa.push(['Ispis ' + trenutak + '   ·   ' + (ctx.brojStavaka || 0) + ' stavaka'].concat(prazno.slice(1)))
  aoa.push(['Početno stanje ' + novacTekst(ctx.psSaldo) +
    '   ·   Promet duguje ' + novacTekst(ctx.prometD) +
    '   ·   Promet potražuje ' + novacTekst(ctx.prometP) +
    '   ·   Saldo ' + novacTekst(ctx.saldoKraj)].concat(prazno.slice(1)))
  aoa.push(prazno.slice())

  const redakNaslova = aoa.length
  aoa.push(cols.map(c => c.head))

  const grupe = []
  if (ctx.psGrupa) { grupe.push(ctx.psGrupa) }
  ;(ctx.godine || []).forEach(g => grupe.push(g))

  grupe.forEach(g => {
    g.stavke.forEach(s => {
      aoa.push(cols.map(c => {
        if (c.key === 'debit' || c.key === 'credit') { return iznos(s[c.key]) }
        if (c.key === 'saldo') {
          return s.saldo === null || s.saldo === undefined ? null : Math.round(num(s.saldo) * 100) / 100
        }
        if (c.key === 'dana') { return s.dana === null || s.dana === undefined ? null : s.dana }
        if (c.key === 'opis') { return (s.opis || '') }
        return s[c.key] || ''
      }))
    })
    // medjuzbroj grupe
    const zbroj = prazno.slice()
    zbroj[0] = g.naziv + ' ukupno'
    zbroj[cols.length - 3] = num(g.d)
    zbroj[cols.length - 2] = num(g.p)
    zbroj[cols.length - 1] = num(g.saldo)
    aoa.push(zbroj)
  })

  const ukupno = prazno.slice()
  ukupno[0] = 'UKUPNO ' + valuta
  ukupno[cols.length - 3] = num(ctx.ukupnoD)
  ukupno[cols.length - 2] = num(ctx.ukupnoP)
  ukupno[cols.length - 1] = num(ctx.saldoKraj)
  aoa.push(ukupno)

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({wch: c.w}))
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range(
      {r: redakNaslova, c: 0},
      {r: aoa.length - 1, c: cols.length - 1}
    )
  }
  // Dana je cijeli broj, iznosi imaju dvije decimale - format se stavlja samo
  // na tri iznosne kolone.
  formatirajBrojeve(ws, redakNaslova + 1, cols.length - 3)

  spremi(
    ws,
    'Kartica ' + String(partner.ime || '').substring(0, 20),
    (company.CompanyNameShort || 'Kartica') + '_Kartica_' +
      String(partner.ime || 'partner').replace(/[^A-Za-z0-9]/g, '').substring(0, 20) +
      '_' + dayjs().format('DD_MM_YYYY_HHmm')
  )
}

// ---------------------------------------------------------------------------
// Otvorene stavke - Par.Otvoreno i Svi part.otv.
// ---------------------------------------------------------------------------
//
// Otvoreno i saldo idu s predznakom: racun +, uplata -. Saldo = otvoreni
// racuni - nepovezane uplate, isto kao na ekranu.

const koloneOtvorenih = () => [
  {key: 'vrsta', head: 'Vrsta', w: 8},
  {key: 'dokument', head: 'Dokument', w: 14},
  {key: 'veza', head: 'Veza', w: 13},
  {key: 'datumTxt', head: 'Datum knjiženja', w: 16},
  {key: 'dospijeceTxt', head: 'Dospijeće', w: 13},
  {key: 'dana', head: 'Dana od dospijeća', w: 10},
  {key: 'opis', head: 'Opis', w: 40},
  {key: 'iznos', head: 'Iznos', w: 14},
  {key: 'zatvoreno', head: 'Zatvoreno', w: 14},
  {key: 'otvoreno', head: 'Otvoreno', w: 14},
  {key: 'saldo', head: 'Saldo', w: 15}
]

const zaokruzi = (v) => Math.round(num(v) * 100) / 100

/**
 * @param {Object} ctx  isti kao za pdf otvorenih stavaka partnera
 */
export const exportOpenItemsXlsx = (ctx) => {
  const cols = koloneOtvorenih()
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const partner = ctx.partner || {}
  const z = ctx.sazetak || {}
  const redci = ctx.redci || []
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const prazno = cols.map(() => null)
  const red = (t) => [t].concat(prazno.slice(1))
  const aoa = []
  aoa.push(red(ctx.naslov || 'Otvorene stavke'))
  aoa.push(red(partner.ime || ''))
  aoa.push(red(partner.oib ? 'OIB: ' + partner.oib : ''))
  aoa.push(red(naziviFirme(company)[0]))
  aoa.push(red(valuta + '   stanje na ' + datumTekst(ctx.doDatuma || new Date()) +
    '   konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '')))
  aoa.push(red('Ispis ' + trenutak + '   ·   ' + redci.length + ' stavaka' + (ctx.filtar ? '   ·   prikaz: ' + ctx.filtar : '')))
  aoa.push(red('Otvoreni računi ' + novacTekst(z.iznosRacuna) + ' (' + (z.racuna || 0) + ')' +
    '   ·   Nepovezane uplate ' + novacTekst(z.iznosUplata) + ' (' + (z.uplata || 0) + ')' +
    '   ·   Saldo (računi − uplate) ' + novacTekst(z.saldo)))
  aoa.push(prazno.slice())

  const redakNaslova = aoa.length
  aoa.push(cols.map(c => c.head))
  redci.forEach(r => {
    aoa.push(cols.map(c => {
      if (c.key === 'iznos') { return zaokruzi(r.iznos) }
      if (c.key === 'zatvoreno') { return iznos(r.zatvoreno) }
      if (c.key === 'otvoreno') { return zaokruzi(r.jeRacun ? r.otvoreno : -r.otvoreno) }
      if (c.key === 'saldo') { return zaokruzi(r.saldo) }
      if (c.key === 'dana') { return r.dana === null || r.dana === undefined ? null : r.dana }
      return r[c.key] || ''
    }))
  })
  const saldo = redci.length ? redci[redci.length - 1].saldo : 0
  const ukupno = prazno.slice()
  ukupno[0] = 'UKUPNO ' + valuta
  ukupno[cols.length - 2] = zaokruzi(saldo)
  ukupno[cols.length - 1] = zaokruzi(saldo)
  aoa.push(ukupno)

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({wch: c.w}))
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({r: redakNaslova, c: 0}, {r: Math.max(redakNaslova, aoa.length - 2), c: cols.length - 1})
  }
  formatirajBrojeve(ws, redakNaslova + 1, 7)

  spremi(
    ws,
    'Otvorene ' + String(partner.ime || '').substring(0, 20),
    (company.CompanyNameShort || 'Otvorene') + '_Otvorene_' +
      String(partner.ime || 'partner').replace(/[^A-Za-z0-9]/g, '').substring(0, 20) +
      '_' + dayjs().format('DD_MM_YYYY_HHmm')
  )
}

const koloneOtvorenoPartneri = () => [
  {key: 'partnerName', head: 'Partner', w: 36},
  {key: 'partnerVATID', head: 'OIB', w: 13},
  {key: 'racuna', head: 'Broj računa', w: 8},
  {key: 'iznosRacuna', head: 'Otvoreni računi', w: 15, novac: true},
  {key: 'uplata', head: 'Broj uplata', w: 8},
  {key: 'iznosUplata', head: 'Nepovezane uplate', w: 15, novac: true},
  {key: 'saldo', head: 'Saldo (računi − uplate)', w: 16, novac: true},
  {key: 'nedospjelo', head: 'Nedospjelo', w: 14, novac: true},
  {key: 'd1_30', head: '1–30 dana', w: 13, novac: true},
  {key: 'd31_60', head: '31–60 dana', w: 13, novac: true},
  {key: 'd61_90', head: '61–90 dana', w: 13, novac: true},
  {key: 'preko90', head: 'Više od 90 dana', w: 14, novac: true}
]

/**
 * @param {Object} ctx  isti kao za pdf liste partnera
 */
export const exportOpenPartnersXlsx = (ctx) => {
  const cols = koloneOtvorenoPartneri()
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const partneri = ctx.partneri || []
  const z = ctx.ukupno || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const prazno = cols.map(() => null)
  const red = (t) => [t].concat(prazno.slice(1))
  const aoa = []
  aoa.push(red(ctx.naslov || 'Otvorene stavke'))
  aoa.push(red(naziviFirme(company)[0]))
  aoa.push(red(valuta + '   stanje na ' + datumTekst(ctx.doDatuma || new Date()) +
    '   konta ' + (ctx.fromAccount || '') + '–' + (ctx.toAccount || '')))
  aoa.push(red('Ispis ' + trenutak + '   ·   ' + partneri.length + ' partnera' + (ctx.trazi ? '   ·   traži: ' + ctx.trazi : '')))
  aoa.push(prazno.slice())

  const redakNaslova = aoa.length
  aoa.push(cols.map(c => c.head))
  const vrijednost = (p, c) => {
    if (c.key === 'saldo') { return zaokruzi(p.saldo) }
    if (c.novac) { return iznos(p[c.key]) }
    if (c.key === 'racuna' || c.key === 'uplata') { return p[c.key] || null }
    return p[c.key] || ''
  }
  partneri.forEach(p => aoa.push(cols.map(c => vrijednost(p, c))))
  aoa.push(cols.map(c => vrijednost(Object.assign({}, z, {partnerName: 'UKUPNO ' + valuta, partnerVATID: ''}), c)))

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({wch: c.w}))
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({r: redakNaslova, c: 0}, {r: Math.max(redakNaslova, aoa.length - 2), c: cols.length - 1})
  }
  // Broj racuna i uplata ostaju cijeli brojevi; format s decimalama samo na iznose.
  const raspon = XLSX.utils.decode_range(ws['!ref'])
  for (let r = redakNaslova + 1; r <= raspon.e.r; r += 1) {
    cols.forEach((c, i) => {
      if (!c.novac) { return }
      const cell = ws[XLSX.utils.encode_cell({r: r, c: i})]
      if (cell && cell.t === 'n') { cell.z = FMT }
    })
  }

  spremi(
    ws,
    'Otvorene stavke',
    (company.CompanyNameShort || 'Otvorene') + '_Otvorene_svi_' + dayjs().format('DD_MM_YYYY_HHmm')
  )
}

// ---------------------------------------------------------------------------
// Dnevnik knjizenja
// ---------------------------------------------------------------------------
//
// Dva lista: Dokumenti (kao na ekranu) i Stavke (sve stavke tih dokumenata).
// Stavka nosi redni broj dokumenta, pa se listovi mogu povezati filtrom.

const koloneDnevnikaDokumenti = () => [
  {key: 'rb', head: 'Rb.', w: 8},
  {key: 'datumTxt', head: 'Datum knjiženja', w: 14},
  {key: 'vrsta', head: 'Vrsta', w: 13},
  {key: 'oznaka', head: 'Dokument', w: 16},
  {key: 'partnerName', head: 'Partner', w: 30},
  {key: 'partnerVATID', head: 'OIB partnera', w: 13},
  {key: 'opis', head: 'Opis', w: 36},
  {key: 'stavaka', head: 'Stavaka', w: 8},
  {key: 'duguje', head: 'Duguje', w: 15, novac: true},
  {key: 'potrazuje', head: 'Potražuje', w: 15, novac: true},
  {key: 'razlika', head: 'Razlika (D − P)', w: 14, novac: true},
  {key: 'knjizio', head: 'Knjižio', w: 22},
  {key: 'email', head: 'Email', w: 26},
  {key: 'unesenoTxt', head: 'Uneseno', w: 16}
]

const koloneDnevnikaStavke = () => [
  {key: 'rb', head: 'Rb. dokumenta', w: 8},
  {key: 'datumTxt', head: 'Datum knjiženja', w: 14},
  {key: 'oznaka', head: 'Dokument', w: 16},
  {key: 'account4', head: 'Konto', w: 8},
  {key: 'naziv', head: 'Naziv konta', w: 34},
  {key: 'Description', head: 'Opis', w: 36},
  {key: 'partnerName', head: 'Partner', w: 28},
  {key: 'partnerVATID', head: 'OIB partnera', w: 13},
  {key: 'veza', head: 'Veza', w: 12},
  {key: 'duguje', head: 'Duguje', w: 15, novac: true},
  {key: 'potrazuje', head: 'Potražuje', w: 15, novac: true}
]

// List s naslovnim recima, tablicom i po zelji zbrojem ispod (izvan filtra).
const listDnevnika = (naslovni, cols, redci, ukupno) => {
  const prazno = cols.map(() => null)
  const aoa = naslovni.map(t => [t].concat(prazno.slice(1)))
  aoa.push(prazno.slice())
  const redakNaslova = aoa.length
  aoa.push(cols.map(c => c.head))
  redci.forEach(r => aoa.push(cols.map(c => (r[c.key] === undefined ? null : r[c.key]))))
  const zadnjiUFiltru = aoa.length - 1
  if (ukupno) { aoa.push(cols.map(c => (ukupno[c.key] === undefined ? null : ukupno[c.key]))) }

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({wch: c.w}))
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({r: redakNaslova, c: 0}, {r: Math.max(redakNaslova, zadnjiUFiltru), c: cols.length - 1})
  }
  const raspon = XLSX.utils.decode_range(ws['!ref'])
  for (let r = redakNaslova + 1; r <= raspon.e.r; r += 1) {
    cols.forEach((c, i) => {
      if (!c.novac) { return }
      const cell = ws[XLSX.utils.encode_cell({r: r, c: i})]
      if (cell && cell.t === 'n') { cell.z = FMT }
    })
  }
  return ws
}

/**
 * @param {Object} ctx  isti kao za pdf dnevnika, plus stavke {idDokumenta: [stavke]}
 */
export const exportJournalXlsx = (ctx) => {
  const company = ctx.company || {}
  const valuta = ctx.valuta || 'EUR'
  const dokumenti = ctx.dokumenti || []
  const z = ctx.zbroj || {}
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const naslovni = [
    'Dnevnik knjiženja',
    naziviFirme(company)[0],
    valuta + '   ' + (ctx.razdoblje || '') + '   poslovna godina ' + (ctx.godina || ''),
    'Ispis ' + trenutak + '   ·   ' + (ctx.redoslijed || '') + (ctx.filtar ? '   ·   ' + ctx.filtar : ''),
    'Dokumenata ' + dokumenti.length + '   ·   Duguje ' + novacTekst(z.duguje) + '   ·   Potražuje ' + novacTekst(z.potrazuje)
  ]

  const redciDokumenata = dokumenti.map(d => Object.assign({}, d, {
    vrsta: d.vrsta ? d.vrsta.naziv : '',
    duguje: zaokruzi(d.duguje),
    potrazuje: zaokruzi(d.potrazuje),
    razlika: iznos(d.razlika)
  }))
  const ukupno = {
    rb: 'UKUPNO ' + valuta,
    stavaka: z.stavaka || 0,
    duguje: zaokruzi(z.duguje),
    potrazuje: zaokruzi(z.potrazuje),
    razlika: iznos(z.razlika)
  }

  const redciStavaka = []
  dokumenti.forEach(d => {
    ;((ctx.stavke || {})[d.id] || []).forEach(s => {
      redciStavaka.push(Object.assign({}, s, {
        rb: d.rb,
        datumTxt: d.datumTxt,
        oznaka: d.oznaka,
        duguje: iznos(s.duguje),
        potrazuje: iznos(s.potrazuje)
      }))
    })
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, listDnevnika(naslovni, koloneDnevnikaDokumenti(), redciDokumenata, ukupno), 'Dokumenti')
  XLSX.utils.book_append_sheet(wb, listDnevnika(naslovni, koloneDnevnikaStavke(), redciStavaka, null), 'Stavke')
  XLSX.writeFile(wb, (company.CompanyNameShort || 'Dnevnik') + '_Dnevnik_' + dayjs().format('DD_MM_YYYY_HHmm') + '.xlsx')
}

export default {
  exportJournalXlsx,
  exportBalanceXlsx, exportAccountCardXlsx, exportStatementXlsx, exportPartnerCardXlsx,
  exportOpenItemsXlsx, exportOpenPartnersXlsx
}
